import { stripAttributes } from './CsSanitizer';
import { tokenizeCs } from './CsTokenizer';
import { scanCsTypes } from './CsTypeScanner';
import { scanCsMembers } from './CsMemberScanner';
import { getMemberSmells } from './CsMemberSmells';
import { scanNamespace } from './CsNamespaceScanner';

/**
 * When a member has no description of its own, but this type inherits from
 * another type parsed in the SAME paste (e.g. a class implementing an
 * interface whose members carry the XMLDoc), copy that base member's
 * description over by matching member name. Base types not present in the
 * pasted source (e.g. a base class from another file) are silently skipped
 * — nothing to copy from, `inherits` just holds a name with no match.
 * @param {object[]} parsedTypes
 */
function inheritMemberDescriptions(parsedTypes) {
  const byName = new Map(parsedTypes.map(function toEntry(t) { return [t.name, t]; }));
  parsedTypes.forEach(function fillType(type) {
    (type.inherits || []).forEach(function fillFromBase(baseName) {
      const base = byName.get(baseName);
      if (!base) return;
      type.members.forEach(function fillMember(member) {
        if (member.description) return;
        const baseMember = base.members.find(function sameName(m) { return m.name === member.name; });
        if (baseMember && baseMember.description) {
          member.description = baseMember.description;
        }
      });
    });
  });
}

/**
 * Runs the CsMemberSmells rules against every member of every type. Runs
 * AFTER inheritMemberDescriptions on purpose — a rule like "missing comment"
 * must see the FINAL description (incl. anything just copied from a base
 * type), not flag a member that's about to get its description filled in.
 * @param {object[]} parsedTypes
 */
function applyMemberSmells(parsedTypes) {
  parsedTypes.forEach(function fillType(type) {
    type.members.forEach(function fillMember(member) {
      member.smells = getMemberSmells(member);
    });
  });
}

/**
 * Parses ALL class/interface declarations found in pasted C# source.
 * Pipeline: strip attributes -> tokenize -> find type blocks -> scan members
 * per block. Token-based, not regex-on-raw-text — whitespace, generics and
 * where-clauses don't trip it up the way a single regex did before.
 * @param {string} source - pasted C# source, may contain multiple types
 * @returns {object[]} array of parsed types ({name, stereotype, description,
 *   members, skipped, inherits}), plus a `.namespace` (string|null) property
 *   attached to the array itself — array shape stays intact for existing
 *   callers (.forEach/.flatMap etc in CsImportStrategy.js), the namespace
 *   just rides along as extra metadata.
 */
export function parseCsClasses(source) {
  const sanitized = stripAttributes(source);
  const { tokens, docComments } = tokenizeCs(sanitized);
  const namespace = scanNamespace(tokens);
  const types = scanCsTypes(tokens, docComments);

  const parsed = types.map(function parseType(type) {
    const { members, skipped } = scanCsMembers(type.bodyTokens, type.name, docComments, type.bodyOffset);
    let stereotype = null;
    if (type.kind === 'interface') {
      stereotype = 'interface';
    } else if (type.isAbstract) {
      stereotype = 'abstract';
    }
    return { name: type.name, stereotype, description: type.description, members, skipped, inherits: type.inherits };
  });

  inheritMemberDescriptions(parsed);
  applyMemberSmells(parsed);

  parsed.namespace = namespace;
  return parsed;
}
